import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useCreateRPNEmployees,
  useUpdateRPNByEmployeeId,
} from '@nxsys/hooks/api/employee/useEmployeeApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetRPNUpdateEmployeeListParams } from '@nxsys/store/slices/employee/types';
import type { RPNErrorReason, RPNRequestStatus } from '@nxsys/store/slices/rpn/types';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { showToast } from '@nxsys/utils/toast';
import { Descriptions, Card, Col, Row, Tag, Alert } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  employeeId: string;
  readOnly?: boolean;
};

type RPNRecord = {
  id: string;
  type?: 'REQUEST' | 'PULL'; // NX-5262: request-new vs pull row
  subcontractor?: {
    forename?: string;
    surname?: string;
  };
  employerName?: string;
  employerRegistrationNumber?: string;
  taxYear?: number;
  totalRPNCount?: number;
  rpns?: Array<{
    rpnNumber?: string;
    rpnIssueDate?: string;
    effectiveDate?: string;
    endDate?: string;
    employeeID?: {
      employeePpsn?: string;
      employmentID?: string;
    };
    incomeTaxCalculationBasis?: string;
    yearlyTaxCredits?: number;
    taxRates?: Array<{ yearlyRateCutOff?: number }>;
    uscStatus?: string;
    uscRates?: Array<{ yearlyUSCRateCutOff?: number }>;
  }>;
  updatedAt?: string;
  errorMessage?: string;
  isSuccess?: boolean;
};

const RPN_ERROR_REASON_LABELS: Record<RPNErrorReason, string> = {
  CERT: 'certificate',
  REVENUE_SETUP: 'revenue not set up',
  INVALID_PPS: 'invalid PPS',
  LEAVER: 'leaver',
  NOT_READY: 'awaiting Revenue',
  OTHER: 'error',
};

type RPNStatusNoticeProps = {
  status: RPNRequestStatus | null | undefined;
  errorReason: RPNErrorReason | null | undefined;
};

// Explains the employee's RPN state so the user understands what's happening and
// what (if anything) they need to do — instead of a bare status chip.
const RPNStatusNotice: React.FC<RPNStatusNoticeProps> = ({
  status,
  errorReason,
}) => {
  // Awaiting Revenue: REQUESTED, or ERROR with NOT_READY (registered, no RPN yet).
  const isAwaiting =
    status === 'REQUESTED' || (status === 'ERROR' && errorReason === 'NOT_READY');

  if (isAwaiting) {
    return (
      <Alert
        type="info"
        showIcon
        message="RPN requested — awaiting Revenue"
        description="We've asked Revenue for this employee's RPN. Revenue hasn't issued it yet — this is normal for a new starter. The system re-checks automatically twice a day; you can also press “Check RPN now”. Until the RPN arrives, the employee is taxed on an emergency basis."
      />
    );
  }

  if (status === 'RECEIVED') {
    return (
      <Alert
        type="success"
        showIcon
        message="RPN received"
        description="Revenue has issued this employee's RPN and it has been applied to their tax, USC and PRSI settings."
      />
    );
  }

  if (status === 'ERROR') {
    const errorDescriptions: Record<RPNErrorReason, string> = {
      CERT: "The company's ROS certificate (P12 file) or its password is invalid, or the certificate has expired. Update them in the company's Revenue Settings, then press “Check RPN now”.",
      REVENUE_SETUP:
        "This company isn't set up with Revenue yet (missing employer registration number or certificate). Configure Revenue settings before requesting an RPN.",
      INVALID_PPS:
        "Revenue rejected the PPS number / Employment ID for this employee. Check the PPS number on the employee's profile, then try again.",
      // NX-6218: also an employee still active here whose employment Revenue has ended.
      LEAVER:
        'Employee is a leaver in Nxsys or on ROS, so their RPN was not updated. See the error message below.',
      NOT_READY:
        'Revenue has no RPN for this employee yet — awaiting Revenue.',
      OTHER:
        "Something went wrong requesting this employee's RPN. Press “Check RPN now” to retry.",
    };
    const desc = errorReason
      ? (errorDescriptions[errorReason] ?? errorDescriptions.OTHER)
      : errorDescriptions.OTHER;
    const reasonLabel = errorReason
      ? (RPN_ERROR_REASON_LABELS[errorReason] ?? 'error')
      : 'error';
    return (
      <Alert
        type={errorReason === 'LEAVER' ? 'warning' : 'error'}
        showIcon
        message={`RPN not available — ${reasonLabel}`}
        description={desc}
      />
    );
  }

  // NONE / unknown: no RPN yet, will be requested.
  return (
    <Alert
      type="warning"
      showIcon
      message="No RPN yet"
      description="Revenue hasn't issued an RPN for this employee. They'll be taxed on an emergency basis until one is received. The system requests an RPN automatically for eligible Ireland workers; you can also press “Check RPN now”."
    />
  );
};

const RPNUpdateTab: React.FC<Props> = ({ employeeId, readOnly }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();

  const { rpnUpdateList, employeeDetail } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list, pagination, queryParams } = rpnUpdateList;

  // rpnRequestStatus / rpnErrorReason live on the worker entity.
  // NOTE: These fields are expected from the backend employee detail API.
  // If the backend DTO does not expose them yet, the tag will render "No RPN" by default.
  const workerData = employeeDetail?.data as (typeof employeeDetail.data & {
    rpnRequestStatus?: RPNRequestStatus;
    rpnErrorReason?: RPNErrorReason;
  }) | null;
  const rpnRequestStatus = workerData?.rpnRequestStatus ?? null;
  const rpnErrorReason = workerData?.rpnErrorReason ?? null;

  const { run: updateRPN, loading: isUpdatingRPN } = useUpdateRPNByEmployeeId();
  const { run: createRPN, loading: isCreatingRPN } = useCreateRPNEmployees();

  const [detailRecord, setDetailRecord] = useState<RPNRecord | null>(null);

  useEffect(() => {
    handleFetchData({ page: 0, size: 20 });
  }, []);

  const handleFetchData = (params: GetRPNUpdateEmployeeListParams) => {
    dispatch(
      employeeActions.getRPNUpdateEmployeeList({ ...params, id: employeeId }),
    );
  };

  // The notice and the button label read rpnRequestStatus off the worker entity,
  // which lives in a different slice than the history list. Refreshing only the
  // list left a successful check still showing "awaiting Revenue" until reload.
  const refreshRpnState = () => {
    handleFetchData({ ...queryParams, page: 0, size: 20 });
    dispatch(employeeActions.getEmployeeById(employeeId));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleUpdateRPN = async () => {
    try {
      const taxYear = getTaxWeekTaxYear().taxYear;
      const res = await updateRPN(employeeId, { taxYear });
      // The backend reports what the check concluded:
      //   'REQUESTED'          -> Revenue has nothing yet, we are waiting
      //   any other status     -> we cannot even ask yet (missing PPS Number or
      //                           Revenue setup); saying "awaiting" would be a lie
      //   no status at all     -> an RPN was pulled and applied
      const checkedStatus = (res as { status?: string } | undefined)?.status;
      if (checkedStatus === 'REQUESTED') {
        showToast({
          type: 'info',
          message:
            messages['rpn.toast.awaiting'] ??
            "Checked — Revenue has no RPN for this employee yet. We'll keep checking automatically; they stay on emergency tax until it arrives.",
        });
      } else if (checkedStatus) {
        showToast({
          type: 'warning',
          message:
            messages['rpn.toast.notReady'] ??
            "Checked — Revenue has no RPN for this employee, and we can't request one yet. Add a valid PPS Number and make sure the employer's Revenue details are set up.",
        });
      } else {
        showToast({
          type: 'success',
          message:
            messages['rpn.toast.received'] ??
            'RPN received from Revenue and applied to this employee.',
        });
      }
      refreshRpnState();
    } catch (error: unknown) {
      console.error(error);
      // Surface WHY the check failed (expired cert, revenue not set up, invalid
      // PPS, etc.) instead of failing silently.
      const err = error as {
        message?: string;
        data?: { message?: string };
        response?: { data?: { message?: string } };
      };
      const reason =
        err?.response?.data?.message ??
        err?.data?.message ??
        err?.message ??
        'Please try again.';
      showToast({
        type: 'error',
        message: `Couldn't check RPN: ${reason}`,
      });
      refreshRpnState();
    }
  };

  const handleRequestNew = async () => {
    try {
      const res = await createRPN({ ids: [employeeId] });
      const failed = res?.listEmployeeFailed ?? [];
      if (failed.length > 0) {
        throw new Error(failed[0]?.errorMsg);
      }
      // Check if response indicates REQUESTED status (Revenue has no RPN yet)
      const succeeded = res?.listEmployeeSucceed ?? [];
      const isRequested =
        succeeded.length > 0 &&
        (succeeded[0] as { status?: string }).status === 'REQUESTED';

      if (isRequested) {
        showToast({
          type: 'info',
          message:
            messages['rpn.toast.requested'] ??
            "RPN requested. Awaiting Revenue — we'll retrieve it automatically once issued.",
        });
      } else {
        showMessage({
          type: 'success',
          content: messages['rpn.update.getSuccess'] ?? 'RPN updated successfully!',
        });
      }
      refreshRpnState();
    } catch (error: unknown) {
      console.error(error);
      refreshRpnState();
    }
  };

  const isActionLoading = isUpdatingRPN || isCreatingRPN;

  // The primary action is always a PULL. Only a worker that already RECEIVED an
  // RPN gets "Update RPN" (refresh); everything else (NONE / REQUESTED / ERROR)
  // is "Check RPN now" — matching the notice copy which tells the user to press
  // "Check RPN now" to check/retry.
  const hasReceivedRpn = rpnRequestStatus === 'RECEIVED';
  const updateButtonLabel = hasReceivedRpn
    ? (messages['rpn.updateRPN'] ?? 'Update RPN')
    : (messages['rpn.checkNow'] ?? 'Check RPN now');

  // NX-6218: keyed on the reason the backend stored, not on the message text,
  // so rewording the message cannot hide the button.
  const showRequestNew = rpnErrorReason === 'NOT_READY';

  const columns = [
    {
      key: 'actions',
      title: messages['common.action'],
      width: 100,
      fixed: 'left' as const,
      align: 'left' as const,
      render: (record: RPNRecord) =>
        record.rpns?.length ? (
          <EyeOutlined
            className="cursor-pointer text-primary-500"
            onClick={() => setDetailRecord(record)}
          />
        ) : null,
    },
    {
      key: 'type',
      title: messages['common.type'] ?? 'Type',
      dataIndex: 'type',
      width: 160,
      // Labels match the two action buttons users already know:
      // "Request New" (POST) and "Update RPN" (GET) — avoid dev jargon like "Pull".
      render: (type: RPNRecord['type']) =>
        type === 'REQUEST' ? (
          <Tag color="blue">{messages['rpn.type.request'] ?? 'Request New'}</Tag>
        ) : (
          <Tag>{messages['rpn.type.update'] ?? 'Update'}</Tag>
        ),
    },
    {
      key: 'subcontractor',
      title: messages['common.employeeName'] ?? 'Employee Name',
      dataIndex: 'subcontractor',
      width: 200,
      render: (sub: RPNRecord['subcontractor']) =>
        `${sub?.forename ?? '_'} ${sub?.surname ?? '_'}`,
    },
    {
      key: 'employerName',
      title: messages['common.employerName'] ?? 'Employer Name',
      dataIndex: 'employerName',
      width: 200,
    },
    {
      key: 'employerRegistrationNumber',
      title:
        messages['common.employerRegistrationNumber'] ?? 'Employer Reg. Number',
      dataIndex: 'employerRegistrationNumber',
      width: 200,
    },
    {
      key: 'taxYear',
      title: messages['common.taxYear'],
      dataIndex: 'taxYear',
      width: 100,
    },
    {
      key: 'totalRPNCount',
      title: messages['common.totalRPNCount'] ?? 'Total RPN Count',
      dataIndex: 'totalRPNCount',
      width: 180,
    },
    {
      key: 'dateEffective',
      title: messages['common.dateEffective'] ?? 'Date Effective',
      dataIndex: 'rpns',
      width: 150,
      render: (rpns: RPNRecord['rpns']) =>
        rpns?.[0]?.effectiveDate
          ? moment(rpns[0].effectiveDate).format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'updatedAt',
      title: messages['common.lastUpdated'] ?? 'Last Updated',
      dataIndex: 'updatedAt',
      width: 150,
      render: (val: string) => (val ? moment(val).format('DD/MM/YYYY') : '_'),
    },
    {
      key: 'errorMessage',
      title: messages['common.errorMessage'] ?? 'Error Message',
      dataIndex: 'errorMessage',
      render: (val: string) =>
        val ? <span className="text-red-500">{val}</span> : '_',
    },
  ];

  return (
    <div>
      {/* RPN request status — explanatory notice */}
      <div className="mb-4">
        <RPNStatusNotice
          status={rpnRequestStatus}
          errorReason={rpnErrorReason}
        />
      </div>

      {!readOnly && (
        <div className="flex items-center justify-end gap-2 mb-4">
          <AppButton
            type="primary"
            name={updateButtonLabel}
            onClick={handleUpdateRPN}
            loading={isActionLoading}
          />
          {showRequestNew && (
            <AppButton
              variant="outlined"
              name={messages['rpn.requestNew'] ?? 'Request New'}
              onClick={handleRequestNew}
              loading={isActionLoading}
            />
          )}
        </div>
      )}

      <AppTableContainer
        columns={columns}
        data={list ?? []}
        loading={loading}
        rowKey="id"
        page={pagination.current}
        total={pagination.totalDocs}
        pageSize={pagination.pageSize}
        rowClassName={(record: RPNRecord) =>
          !record.isSuccess ? 'error-row' : ''
        }
        onChangePagination={onChangePagination}
      />

      {/* Detail Modal */}
      <RPNDetailModal
        isOpen={!!detailRecord}
        toggle={() => setDetailRecord(null)}
        data={detailRecord}
      />
    </div>
  );
};

// Detail modal showing RPN details
const RPNDetailModal = ({
  isOpen,
  toggle,
  data,
}: {
  isOpen: boolean;
  toggle: () => void;
  data: RPNRecord | null;
}) => {
  const { messages } = useTypedIntl();

  if (!data?.rpns?.length) {
    return (
      <AppModalContainer
        isOpen={isOpen}
        toggle={toggle}
        title="RPN Detail"
        actions={[
          { name: messages['common.close'] ?? 'Close', onClick: toggle },
        ]}
      >
        <div />
      </AppModalContainer>
    );
  }

  const rpn = data.rpns[0];
  const sub = data.subcontractor;

  const yearlyCutOff =
    rpn.taxRates?.reduce((sum, r) => sum + (r.yearlyRateCutOff ?? 0), 0) ?? 0;

  return (
    <AppModalContainer
      isOpen={isOpen}
      toggle={toggle}
      title={`Issued ${moment(rpn.rpnIssueDate).format('D MMMM YYYY')}`}
      width="60vw"
      actions={[
        {
          name: messages['common.close'] ?? 'Close',
          variant: 'outlined',
          onClick: toggle,
        },
      ]}
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card
            title={messages['common.employmentDetails'] ?? 'Employment Details'}
            size="small"
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label={messages['common.forename']}>
                {sub?.forename ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.surname']}>
                {sub?.surname ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={messages['common.ppsNumber'] ?? 'PPS Number'}
              >
                {rpn.employeeID?.employeePpsn ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={messages['common.employmentId'] ?? 'Employment ID'}
              >
                {rpn.employeeID?.employmentID ?? '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={messages['common.rpnDetails'] ?? 'RPN Details'}
            size="small"
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item
                label={messages['common.rpnNumber'] ?? 'RPN Number'}
              >
                {rpn.rpnNumber ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={messages['common.issueDate'] ?? 'Issue Date'}
              >
                {rpn.rpnIssueDate
                  ? moment(rpn.rpnIssueDate).format('DD/MM/YYYY')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={messages['common.effectiveDate'] ?? 'Effective Date'}
              >
                {rpn.effectiveDate
                  ? moment(rpn.effectiveDate).format('DD/MM/YYYY')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.endDate']}>
                {rpn.endDate ? moment(rpn.endDate).format('DD/MM/YYYY') : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={messages['common.tax']} size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item
                label={
                  messages['common.calculationBasis'] ?? 'Calculation Basis'
                }
              >
                {rpn.incomeTaxCalculationBasis ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  messages['common.annualTaxCredits'] ?? 'Annual Tax Credits'
                }
              >
                {rpn.yearlyTaxCredits ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={messages['common.annualCutOff'] ?? 'Annual Cut-off'}
              >
                {yearlyCutOff}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="USC" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item
                label={messages['common.exemption'] ?? 'Exemption'}
              >
                {rpn.uscStatus === 'ORDINARY' ? 'NOT_EXEMPT' : 'EXEMPT'}
              </Descriptions.Item>
              <Descriptions.Item label="0.5% Annual Cut-off">
                {rpn.uscRates?.[0]?.yearlyUSCRateCutOff ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="2% Annual Cut-off">
                {rpn.uscRates?.[1]?.yearlyUSCRateCutOff ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="3% Annual Cut-off">
                {rpn.uscRates?.[2]?.yearlyUSCRateCutOff ?? 0}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </AppModalContainer>
  );
};

export default RPNUpdateTab;
